import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

interface EditableTextProps {
  defaultValue: string;
  disabled?: boolean;
  onConfirm: (value: string) => void;
}

/**
 * Click-to-edit inline text (replaces the Blueprint EditableText used for
 * the brief title). Enter/blur confirm; Escape reverts.
 */
const EditableText: FunctionComponent<EditableTextProps> = ({
  defaultValue,
  disabled,
  onConfirm,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const confirm = () => {
    setEditing(false);
    onConfirm(value);
    // Parents treat an emptied value as a no-op (see briefNavBar), and since
    // the prop then never changes, the sync effect won't restore the text —
    // revert here so the label never renders blank (Blueprint reverted too).
    if (value.trim().length === 0) {
      setValue(defaultValue);
    }
  };

  const cancel = () => {
    setValue(defaultValue);
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        className="bg-transparent border-0 p-0 m-0 text-inherit font-inherit cursor-text hover:bg-gray-100 rounded-sm"
        onClick={() => !disabled && setEditing(true)}
      >
        {value}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      disabled={disabled}
      className="border-0 border-b-2 border-secondary bg-transparent p-0 m-0 text-inherit focus:outline-hidden focus:ring-0"
      onChange={e => setValue(e.target.value)}
      onBlur={() => confirm()}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          confirm();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
    />
  );
};

export default EditableText;
